import {
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    PencilIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import { Item as DropdownMenuItem } from '@radix-ui/react-dropdown-menu';
import { useState } from 'react';

import { Button } from '@/components/core/Button';
import { ButtonDropdown } from '@/components/core/ButtonDropdown/ButtonDropdown';
import ButtonPanel from '@/components/core/ButtonPanel/ButtonPanel';
import { DialogClose } from '@/components/core/Dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/core/DropdownMenu/DropdownMenu';
import { useTranslation } from '@/hooks/useTranslation';

import { BulkSubscription } from '../bulk-subscriptions.types';

export type BulkSubDownloadFormat = 'excel' | 'json';

interface BulkSubDialogHeaderProps {
    entity: BulkSubscription;
    onRename: () => void;
    onUpload: () => void;
    onDownload?: (format: BulkSubDownloadFormat) => void;
    isUploadDisabled?: boolean;
    isUploadVisible?: boolean;
}

/**
 * Header content for the bulk subscription entries dialog. It shows the
 * subscription label with an inline edit (rename) action, and the file
 * actions (upload / download) plus a close button.
 *
 * The layout is responsive: from the `md` breakpoint the upload and download
 * actions render as labelled buttons; below `md` they collapse to icon-only
 * buttons to save horizontal space.
 */
const BulkSubDialogHeader = ({
    entity,
    onRename,
    onUpload,
    onDownload,
    isUploadDisabled = false,
    isUploadVisible = true,
}: BulkSubDialogHeaderProps) => {

    const { t } = useTranslation();

    const [isDownloadOpen, setIsDownloadOpen] = useState(false);

    const uploadTitle = t('BULK_SUBSCRIPTION.UPLOAD_FILE');
    const downloadTitle = t('BULK_SUBSCRIPTION.DOWNLOAD_AS');

    const downloadOptions: {
        format: BulkSubDownloadFormat;
        shortLabel: string;
        fullLabel: string;
    }[] = [
        { format: 'excel', shortLabel: 'Excel', fullLabel: t('BULK_SUBSCRIPTION.DOWNLOAD_AS_EXCEL') },
        { format: 'json', shortLabel: 'JSON', fullLabel: t('BULK_SUBSCRIPTION.DOWNLOAD_AS_JSON') },
    ];

    const handleDownload = (format: BulkSubDownloadFormat) => {
        setIsDownloadOpen(false);
        onDownload?.(format);
    };

    return (
        <div className="tw-flex tw-items-center tw-gap-3 tw-w-full">
            <div className="tw-flex tw-flex-1 tw-items-center tw-gap-1 tw-min-w-0">
                <span className="tw-font-bold tw-text-t-base lg:tw-text-t-lg tw-text-h-primary tw-truncate">
                    {entity.label || entity.name}
                </span>
                <ButtonPanel
                    isDisabled={false}
                    icon={PencilIcon}
                    tooltip={t('BULK_SUBSCRIPTION.RENAME_ACTION')}
                    onClick={onRename}
                />
            </div>

            <div className="tw-flex tw-items-center tw-gap-3 tw-shrink-0">
                {isUploadVisible && (
                    <>
                        <Button
                            className="tw-hidden md:tw-inline-flex"
                            variant="outline"
                            hasIcon={true}
                            icon={ArrowUpTrayIcon}
                            disabled={isUploadDisabled}
                            title={uploadTitle}
                            onClick={onUpload}
                        >
                            {uploadTitle}
                        </Button>
                        <ButtonPanel
                            className="md:tw-hidden"
                            isDisabled={isUploadDisabled}
                            icon={ArrowUpTrayIcon}
                            tooltip={uploadTitle}
                            onClick={onUpload}
                        />
                    </>
                )}

                <DropdownMenu open={isDownloadOpen} onOpenChange={setIsDownloadOpen}>
                    <DropdownMenuTrigger asChild={true}>
                        <div className="tw-flex tw-items-center">
                            <div className="tw-hidden md:tw-block">
                                <ButtonDropdown
                                    titleOne={downloadTitle}
                                    icon={ArrowDownTrayIcon}
                                    isOpen={isDownloadOpen}
                                    title={downloadTitle}
                                />
                            </div>
                            <ButtonPanel
                                className="md:tw-hidden"
                                isDisabled={false}
                                icon={ArrowDownTrayIcon}
                                tooltip={downloadTitle}
                            />
                        </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        hasBorder={true}
                        hasPadding={false}
                        className="tw-min-w-[8rem]"
                    >
                        {downloadOptions.map(option => (
                            <DropdownMenuItem
                                key={option.format}
                                className="tw-flex tw-items-center tw-px-3 tw-py-2 tw-text-t-sm tw-cursor-pointer tw-whitespace-nowrap
                                tw-outline-none data-[highlighted]:tw-bg-fill-primary-hover hover:tw-bg-fill-primary-hover"
                                onSelect={() => handleDownload(option.format)}
                            >
                                <span className="tw-hidden md:tw-inline">{option.shortLabel}</span>
                                <span className="md:tw-hidden">{option.fullLabel}</span>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>

                <DialogClose asChild={true}>
                    <ButtonPanel
                        isDisabled={false}
                        icon={XMarkIcon}
                        tooltip={t('CLOSE')}
                    />
                </DialogClose>
            </div>
        </div>
    );
};

export { BulkSubDialogHeader };
